import { useState } from "react";
import { Plus, Search, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import {
  useCreateSearchProfile,
  useDeleteSearchProfile,
} from "@/hooks/mutations/useSearchProfileMutations";
import { useTeam } from "@/hooks/queries/useTeam";
import { useCanAutoQualify } from "@/hooks/useCanAutoQualify";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import type { TeamMemberRef } from "@/types/domain";

/** Hasta tres caras y, si hay más, cuántas faltan. */
function AssignedAvatars({ users }: { users: TeamMemberRef[] }) {
  const visible = users.slice(0, 3);
  const hidden = users.length - visible.length;

  return (
    <span
      className="flex shrink-0 -space-x-1.5"
      title={users.map((user) => user.name).join(", ")}
    >
      {visible.map((user) => (
        <MemberAvatar
          key={user.id}
          member={user}
          className="size-5 ring-2 ring-card"
        />
      ))}
      {hidden > 0 && (
        <span className="flex size-5 items-center justify-center rounded-full bg-muted text-[9px] font-bold text-muted-foreground ring-2 ring-card">
          +{hidden}
        </span>
      )}
    </span>
  );
}

export function SearchProfileSidebar() {
  const { profiles, activeProfileId, setActiveProfileId } = useActiveProfile();
  // Las líneas son de la empresa: el rol comercial las usa pero no las administra.
  // Sin esto veía los botones y se comía un 403 sin explicación.
  const { data: team } = useTeam();
  const canManage = !!team?.canManageSearchProfiles;
  const canAutoQualify = useCanAutoQualify();
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [toDelete, setToDelete] = useState<number | null>(null);

  const createMutation = useCreateSearchProfile();
  const deleteMutation = useDeleteSearchProfile();

  const filtered = profiles.filter((p) =>
    p.searchprofile_name.toLowerCase().includes(query.toLowerCase()),
  );

  function handleCreate() {
    if (!newName.trim()) return;
    createMutation.mutate(newName.trim(), {
      onSuccess: (profile) => {
        setActiveProfileId(profile.searchprofile_id);
        setCreateOpen(false);
        setNewName("");
      },
      // El fallo ya lo avisa el hook (toast genérico o modal de mejora). Aquí solo
      // se cierra el diálogo cuando reintentar no sirve de nada: el tope del plan
      // no se resuelve escribiendo otro nombre, y estorba detrás del modal.
      onError: (err) => {
        if (err.errorCode === "SEARCH_PROFILE_LIMIT") {
          setCreateOpen(false);
          setNewName("");
        }
      },
    });
  }

  function handleDelete() {
    if (toDelete == null) return;
    deleteMutation.mutate(toDelete, {
      onSuccess: () => setToDelete(null),
      onError: () => toast.error("No se pudo eliminar la línea de negocio"),
    });
  }

  return (
    <aside className="flex flex-col gap-3 rounded-2xl bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold">Mis líneas de negocio</h2>
        <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-muted-foreground">
          {profiles.length}
        </span>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar línea de negocio..."
          className="pl-8"
        />
      </div>

      <div className="flex flex-col gap-1">
        {filtered.map((p) => {
          const isActive = p.searchprofile_id === activeProfileId;
          return (
            <div
              key={p.searchprofile_id}
              className={cn(
                "group flex items-center gap-2.5 rounded-lg border px-3 py-2.5 transition-colors",
                isActive
                  ? "border-primary bg-primary/5"
                  : "border-transparent hover:bg-accent",
              )}
            >
              <button
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
                onClick={() => setActiveProfileId(p.searchprofile_id)}
              >
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    isActive ? "bg-primary" : "bg-muted-foreground/40",
                  )}
                />
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-semibold">
                    <span className="truncate">{p.searchprofile_name}</span>
                    {canAutoQualify && p.searchprofile_checkqualification && (
                        <span title="Análisis de calificación activo" className="shrink-0">
                          <Zap className="size-3 text-primary" />
                        </span>
                      )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {p.searchprofile_search
                      ? `“${p.searchprofile_search}”`
                      : "Búsqueda global"}
                  </p>
                </div>
              </button>
              {/* Con quién se comparte la línea. Solo la foto: el nombre está a
                  un hover y aquí el espacio es del nombre de la línea. */}
              {(p.assignedUsers?.length ?? 0) > 0 && (
                <AssignedAvatars users={p.assignedUsers!} />
              )}

              {canManage && profiles.length > 1 && (
                <button
                  title="Borrar línea de negocio"
                  className="cursor-pointer opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                  onClick={() => setToDelete(p.searchprofile_id)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="px-3 py-4 text-xs text-muted-foreground">
            No hay líneas de negocio que coincidan.
          </p>
        )}
      </div>

      {canManage ? (
        <Button
          variant="outline"
          className="w-full cursor-pointer border-dashed"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="size-4" />
          Nueva línea de negocio
        </Button>
      ) : (
        <p className="px-3 text-center text-xs text-muted-foreground">
          Las líneas de negocio las administra quien lleva la cuenta.
        </p>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva línea de negocio</DialogTitle>
            <DialogDescription>
              Guarda aquí qué vendes, dónde y con qué filtros.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Ej. Obras en Lima"
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creando..." : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={toDelete != null}
        onOpenChange={(o) => !o && setToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta línea de negocio?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán sus filtros e intereses. Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
